/**
 * Write numbers the way this tenant groups them.
 *
 * `formatNumber` and `formatMoney` are deliberately pure and take the format,
 * so they can be tested and used outside React. Screens should not have to
 * fetch the tenant setting and thread it through every column definition —
 * that is how `'en-IN'` ended up spelled out in 74 places — so the tenant's
 * grouping is bound here, once, from the same display settings the date
 * formatter reads.
 *
 *   const { money, number } = useBusinessNumber();
 *   ...
 *   <>₹{money(p.allocatedAmountInr)}</>
 *   value: (r) => number(r.quantity)
 *
 * The reader's language is deliberately not consulted: a tenant counting in
 * lakh does so whether the product is being read in English or in Hindi.
 */
import { useCallback, useMemo } from 'react';
// Extensionless on purpose — see useBusinessDay.ts.
import { useDisplaySettings } from './useDisplaySettings';
import { formatDecimal, formatMoney, formatNumber } from '../contracts/business-number';

export function useBusinessNumber(): {
  /** An amount of money, always carrying its paise. No currency symbol. */
  readonly money: (value: number | null | undefined) => string;
  /** A quantity, with decimals only when the value has them, or a set number. */
  readonly number: (value: number | null | undefined, fractionDigits?: number) => string;
  /** A stored decimal (quantity, size, rate) exactly as recorded, never rounded. */
  readonly decimal: (value: string | number | null | undefined) => string;
} {
  const { numberFormat } = useDisplaySettings();

  const money = useCallback(
    (value: number | null | undefined) => formatMoney(value, numberFormat),
    [numberFormat],
  );

  const number = useCallback(
    (value: number | null | undefined, fractionDigits?: number) =>
      formatNumber(value, numberFormat, fractionDigits),
    [numberFormat],
  );

  const decimal = useCallback(
    (value: string | number | null | undefined) => formatDecimal(value, numberFormat),
    [numberFormat],
  );

  return useMemo(() => ({ money, number, decimal }), [money, number, decimal]);
}
